import { Linkedin, BookOpen, Sparkles } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useT } from "@/i18n/LanguageProvider";

const LINKEDIN_URL = "https://www.linkedin.com/in/ridhi-tiwari-968b1127a";

export default function Founder() {
    const { t } = useT();
    const r1 = useReveal();
    const r2 = useReveal();

    const credentials = [
        { Icon: Sparkles, label: t("founder.cred1.label"), value: t("founder.cred1.value"), key: "founder" },
        { Icon: BookOpen, label: t("founder.cred2.label"), value: t("founder.cred2.value"), key: "published-author" },
    ];

    return (
        <section
            id="founder"
            data-testid="founder-section"
            aria-labelledby="founder-headline"
            className="relative py-24 md:py-40 bg-background overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-center gap-6 mb-14 md:mb-20">
                    <span aria-hidden="true" className="font-display italic text-5xl md:text-7xl text-primary/25 leading-none">
                        04
                    </span>
                    <div className="text-xs uppercase tracking-[0.3em] text-accent font-semibold">
                        {t("founder.chapter")}
                    </div>
                </div>

                <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                    <div ref={r1} className="reveal md:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-3 md:-inset-4 border border-accent/40 rounded-sm pointer-events-none" aria-hidden="true" />
                            <div className="relative aspect-[3/4] overflow-hidden rounded-sm bg-secondary">
                                <img
                                    src="/media/founder.jpg"
                                    alt="Portrait of Ridhi Tiwari, Founder of CHIGLE"
                                    loading="lazy"
                                    decoding="async"
                                    width="900"
                                    height="1200"
                                    className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out hover:scale-[1.03]"
                                />
                            </div>
                            <div className="absolute -bottom-3 -left-3 bg-primary text-primary-foreground px-5 py-2.5 text-[10px] md:text-xs uppercase tracking-[0.3em] rounded-sm">
                                {t("founder.badge")}
                            </div>
                        </div>
                    </div>

                    <div ref={r2} className="reveal md:col-span-7 md:pl-6">
                        <h2 id="founder-headline" className="font-display text-5xl md:text-6xl lg:text-7xl text-primary tracking-tight leading-[0.95] mb-8">
                            <em className="text-accent">{t("founder.title.accent")}</em> {t("founder.title.last")}
                        </h2>

                        <p
                            style={{ fontFamily: '"Fraunces", "Cormorant Garamond", serif' }}
                            className="text-xl md:text-2xl text-foreground/85 leading-snug mb-6"
                        >
                            {t("founder.intro")}
                        </p>

                        <p className="text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl mb-8">
                            {t("founder.bio")}
                        </p>

                        <blockquote
                            style={{
                                fontFamily: '"Fraunces", "Cormorant Garamond", serif',
                                fontVariationSettings: '"SOFT" 50, "WONK" 0',
                            }}
                            className="italic text-2xl md:text-3xl text-primary leading-snug border-l-2 border-accent pl-5 md:pl-6 mb-10"
                        >
                            {t("founder.quote")}{" "}
                            <span className="text-accent">{t("founder.quote.accent")}</span>
                        </blockquote>

                        <div className="grid sm:grid-cols-2 gap-4 mb-10">
                            {credentials.map((c) => (
                                <div
                                    key={c.key}
                                    data-testid={`founder-credential-${c.key}`}
                                    className="flex items-start gap-3 p-4 border border-primary/15 rounded-sm bg-secondary/40 hover:border-accent/50 hover:bg-secondary/70 transition-all"
                                >
                                    <c.Icon size={18} className="text-accent shrink-0 mt-0.5" strokeWidth={1.6} aria-hidden="true" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-1">
                                            {c.label}
                                        </div>
                                        <div className="text-sm md:text-base text-primary leading-snug">
                                            {c.value}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="founder-linkedin"
                            aria-label="Ridhi Tiwari on LinkedIn (opens in new tab)"
                            className="inline-flex items-center gap-3 text-sm tracking-wide text-primary hover:text-accent transition-colors group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
                        >
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-primary/25 group-hover:border-accent group-hover:bg-accent group-hover:text-primary-foreground transition-all">
                                <Linkedin size={16} />
                            </span>
                            <span className="border-b border-primary/30 group-hover:border-accent pb-0.5">
                                {t("founder.linkedin")}
                            </span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
